/**
 * Procurement dispatch hooks (#3033, T12c1): the status list, the details and the content history of a purchase order's dispatches,
 * the preview, the enqueue, the cancel and the resolve of a dispatch, and the supplier's dispatch settings, on the staff-api routes
 * of T12a (`procurement-dispatch.ts`, `supplier-dispatch-settings.ts`).
 *
 * Contract notes
 *  - The server is the only authority. A status, a document, a hash and a capability shown here come from its answers; nothing
 *    re-implements the dispatch state machine.
 *  - Permissions are exactly the server's: state needs `purchase_orders.read`; preview, content history, enqueue, cancel and resolve
 *    need `purchase_orders.dispatch` (the two never imply each other); settings are read with `inventory.read` and written with
 *    `inventory.update` AND `purchase_orders.dispatch`. A hook without its permission sends nothing and returns no data.
 *  - Query keys carry the tenant, the user and the session family. A read goes through `runOwned`: the owner, the generation and the
 *    permissions are re-read from the auth store just before the request leaves and again when the answer arrives, so an old owner's
 *    answer never fills a cache or a screen.
 *  - Enqueue is a keyed command: the exact body (channel, operation key, reviewed hash) is written to the owner's journal before it is
 *    sent, survives an unmount and a reload, and is only ever resent unchanged and on request. Cancel, resolve and the settings save
 *    have no durable identity: after an unknown outcome they are NOT repeated; the state is refreshed and the outcome is shown as
 *    unknown until the user acknowledges it.
 *  - Nothing here sends a request on mount other than the reads, and no write is ever retried on its own.
 */

import { useEffect, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  DISPATCH_PERMISSIONS,
  DispatchAccessError,
  LateResultError,
  OwnerChangedError,
  READ_PERMISSIONS,
  SETTINGS_READ_PERMISSIONS,
  SETTINGS_WRITE_PERMISSIONS,
  fenceHolds,
  isLocalRefusal,
  ownerEquals,
  ownerId,
  runOwned,
  settle,
  takeFence,
  useDispatchAccess,
  type DispatchAccess,
  type DispatchFence,
  type DispatchOwner,
  type Outcome,
} from '@/features/purchase-orders/dispatch/dispatchSession';
import {
  HISTORY_PAGE_SIZE,
  MAX_LOADED_PAGES,
  fetchDispatchHistory,
  fetchDispatchSummary,
  fetchOrderDispatches,
  fetchSupplierDispatchSettings,
  fetchTenantDispatches,
  postDispatchCancel,
  postDispatchEnqueue,
  postDispatchPreview,
  postDispatchResolve,
  putSupplierDispatchSettings,
  type TenantDispatchFilters,
} from '@/features/purchase-orders/dispatch/dispatchApi';
import {
  beginFlight,
  dropCorruptJournal,
  endFlight,
  getJournalView,
  putEntry,
  recordAttempt,
  removeEntry,
  slotOf,
  useJournalView,
  type JournalEntry,
  type JournalStatus,
} from '@/features/purchase-orders/dispatch/dispatchJournal';
import {
  acknowledgeUnconfirmed,
  addUnconfirmed,
  bumpDocumentEpoch,
  getDocumentEpoch,
  hasUnconfirmed,
  useUnconfirmed,
  type UnconfirmedCommand,
  type UnconfirmedKind,
} from '@/features/purchase-orders/dispatch/dispatchLocalState';
import {
  isDefiniteRefusal,
  isUnknownOutcome,
} from '@/features/purchase-orders/dispatch/dispatchOutcome';
import type {
  CursorPage,
  DispatchCancelResult,
  DispatchChannel,
  DispatchEnqueueBody,
  DispatchEnqueueResult,
  DispatchEvent,
  DispatchHistoryPage,
  DispatchPreview,
  DispatchResolveResult,
  DispatchStatus,
  DispatchSummary,
  ResolveDispatchBody,
  SupplierDispatchSettings,
  SupplierDispatchSettingsBody,
} from '@/features/purchase-orders/dispatch/dispatchTypes';

export { DispatchAccessError, OwnerChangedError, useDispatchAccess };

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const DISPATCH_QUERY_ROOT = 'po-dispatch';

const ownerSegments = (owner: DispatchOwner): string[] => [
  owner.tenantId,
  owner.userId,
  owner.sessionFamilyId,
];

export const dispatchKeys = {
  root: [DISPATCH_QUERY_ROOT] as const,
  owner: (owner: DispatchOwner) => [DISPATCH_QUERY_ROOT, ...ownerSegments(owner)] as const,
  order: (owner: DispatchOwner, purchaseOrderId: string) =>
    [DISPATCH_QUERY_ROOT, ...ownerSegments(owner), 'order', purchaseOrderId] as const,
  orderList: (owner: DispatchOwner, purchaseOrderId: string) =>
    [...dispatchKeys.order(owner, purchaseOrderId), 'list'] as const,
  summary: (owner: DispatchOwner, purchaseOrderId: string, dispatchId: string) =>
    [...dispatchKeys.order(owner, purchaseOrderId), 'summary', dispatchId] as const,
  history: (owner: DispatchOwner, purchaseOrderId: string, dispatchId: string) =>
    [...dispatchKeys.order(owner, purchaseOrderId), 'history', dispatchId] as const,
  tenant: (owner: DispatchOwner) =>
    [DISPATCH_QUERY_ROOT, ...ownerSegments(owner), 'tenant'] as const,
  tenantList: (owner: DispatchOwner, filters: TenantDispatchFilters) =>
    [...dispatchKeys.tenant(owner), filters] as const,
  settings: (owner: DispatchOwner, supplierId: string) =>
    [DISPATCH_QUERY_ROOT, ...ownerSegments(owner), 'settings', supplierId] as const,
};

const isOwnerQuery = (key: readonly unknown[], owner: DispatchOwner): boolean =>
  key[0] === DISPATCH_QUERY_ROOT &&
  key[1] === owner.tenantId &&
  key[2] === owner.userId &&
  key[3] === owner.sessionFamilyId;

/**
 * Keeps the cache to what the live owner may see: every dispatch query of another owner (tenant, user or login) is removed, the
 * content (history) goes when the dispatch permission is lost, and the settings go when their read permission is lost.
 */
export function useDispatchCacheGuard(): void {
  const client = useQueryClient();
  const access = useDispatchAccess();
  const { owner, active, canDispatch, canReadSettings, canReadStatus } = access;
  useEffect(() => {
    const cache = client.getQueryCache();
    for (const query of cache.findAll({ queryKey: dispatchKeys.root })) {
      const key = query.queryKey;
      const mine = owner !== null && active && isOwnerQuery(key, owner);
      const kind = key[4] === 'order' ? key[6] : key[4];
      const allowed =
        kind === 'history'
          ? canDispatch
          : kind === 'settings'
            ? canReadSettings
            : kind === 'list' || kind === 'summary' || kind === 'tenant'
              ? canReadStatus
              : true;
      if (!mine || !allowed) cache.remove(query);
    }
  }, [client, owner, active, canDispatch, canReadSettings, canReadStatus]);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface PagedRead<T> {
  items: T[];
  /** `null` until a first answer arrived. */
  isLoading: boolean;
  isError: boolean;
  /** The last refresh failed while earlier data is still shown: that data may be out of date. */
  isStale: boolean;
  error: unknown;
  isRefetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  /** The bound on loaded pages was reached while the server still has more. */
  overflow: boolean;
  /** When the data shown was last confirmed by the server (epoch ms); 0 before a first answer. */
  updatedAt: number;
  enabled: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

function usePagedRead<T, TPage extends CursorPage<T>>(config: {
  key: readonly unknown[] | null;
  allowed: boolean;
  permissions: readonly string[];
  load: (cursor: string | null) => Promise<TPage>;
}): PagedRead<T> & { pages: TPage[] } {
  const access = useDispatchAccess();
  const enabled = access.active && access.owner !== null && config.allowed && config.key !== null;
  const query = useInfiniteQuery({
    queryKey: config.key ?? [DISPATCH_QUERY_ROOT, 'disabled'],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => runOwned(access, config.permissions, () => config.load(pageParam)),
    getNextPageParam: (last, all) =>
      all.length < MAX_LOADED_PAGES && last.pagination.hasMore
        ? last.pagination.nextCursor
        : undefined,
    refetchOnWindowFocus: false,
    retry: false,
  });
  // Cached data of a key whose permission was lost is never returned.
  const pages = enabled ? (query.data?.pages ?? []) : [];
  const last = pages[pages.length - 1];
  const overflow = pages.length >= MAX_LOADED_PAGES && last?.pagination.hasMore === true;
  return {
    pages,
    items: pages.flatMap((page) => page.data),
    isLoading: enabled && query.isPending,
    isError: enabled && query.isError,
    isStale: enabled && query.isError && pages.length > 0,
    error: enabled ? query.error : null,
    isRefetching: enabled && query.isRefetching && !query.isFetchingNextPage,
    hasNextPage: enabled && !overflow && query.hasNextPage,
    isFetchingNextPage: enabled && query.isFetchingNextPage,
    isFetchNextPageError: enabled && query.isFetchNextPageError,
    overflow,
    updatedAt: enabled ? query.dataUpdatedAt : 0,
    enabled,
    fetchNextPage: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}

/** The dispatches of one purchase order, newest first (`purchase_orders.read`). */
export function useOrderDispatches(purchaseOrderId: string | null): PagedRead<DispatchSummary> {
  const access = useDispatchAccess();
  const {
    items,
    pages: _pages,
    ...rest
  } = usePagedRead<DispatchSummary, CursorPage<DispatchSummary>>({
    key:
      access.owner && purchaseOrderId
        ? dispatchKeys.orderList(access.owner, purchaseOrderId)
        : null,
    allowed: access.canReadStatus && purchaseOrderId !== null,
    permissions: READ_PERMISSIONS,
    load: (cursor) => fetchOrderDispatches(purchaseOrderId ?? '', cursor),
  });
  return { items, ...rest };
}

/** The tenant's dispatches, newest first, optionally by status (`purchase_orders.read`). */
export function useTenantDispatches(
  filters: { status?: DispatchStatus; purchaseOrderId?: string } = {}
): PagedRead<DispatchSummary> {
  const access = useDispatchAccess();
  const normalized: TenantDispatchFilters = {
    status: filters.status,
    purchaseOrderId: filters.purchaseOrderId,
  };
  const {
    items,
    pages: _pages,
    ...rest
  } = usePagedRead<DispatchSummary, CursorPage<DispatchSummary>>({
    key: access.owner ? dispatchKeys.tenantList(access.owner, normalized) : null,
    allowed: access.canReadStatus,
    permissions: READ_PERMISSIONS,
    load: (cursor) => fetchTenantDispatches(normalized, cursor),
  });
  return { items, ...rest };
}

export interface SingleRead<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The last refresh failed while earlier data is still shown. */
  isStale: boolean;
  error: unknown;
  isRefetching: boolean;
  updatedAt: number;
  refetch: () => void;
}

function useSingleRead<T>(config: {
  key: readonly unknown[] | null;
  allowed: boolean;
  permissions: readonly string[];
  load: () => Promise<T>;
}): SingleRead<T> {
  const access = useDispatchAccess();
  const enabled = access.active && access.owner !== null && config.allowed && config.key !== null;
  const query = useQuery({
    queryKey: config.key ?? [DISPATCH_QUERY_ROOT, 'disabled'],
    enabled,
    queryFn: () => runOwned(access, config.permissions, config.load),
    // A single state or setting is cheap to read and changes under people's hands: coming back to the tab confirms it again.
    refetchOnWindowFocus: true,
    retry: false,
  });
  const data = enabled ? query.data : undefined;
  return {
    data,
    isLoading: enabled && query.isPending,
    isError: enabled && query.isError,
    isStale: enabled && query.isError && data !== undefined,
    error: enabled ? query.error : null,
    isRefetching: enabled && query.isRefetching,
    updatedAt: enabled ? query.dataUpdatedAt : 0,
    refetch: () => void query.refetch(),
  };
}

/** One dispatch's state (`purchase_orders.read`). */
export function useDispatchSummary(
  purchaseOrderId: string | null,
  dispatchId: string | null
): SingleRead<DispatchSummary> {
  const access = useDispatchAccess();
  return useSingleRead({
    key:
      access.owner && purchaseOrderId && dispatchId
        ? dispatchKeys.summary(access.owner, purchaseOrderId, dispatchId)
        : null,
    allowed: access.canReadStatus && purchaseOrderId !== null && dispatchId !== null,
    permissions: READ_PERMISSIONS,
    load: () => fetchDispatchSummary(purchaseOrderId ?? '', dispatchId ?? ''),
  });
}

/** The frozen document, the recipient, references and the audit events (`purchase_orders.dispatch`; cost-bearing). */
export function useDispatchHistory(purchaseOrderId: string | null, dispatchId: string | null) {
  const access = useDispatchAccess();
  const read = usePagedRead<DispatchEvent, DispatchHistoryPage & CursorPage<DispatchEvent>>({
    key:
      access.owner && purchaseOrderId && dispatchId
        ? dispatchKeys.history(access.owner, purchaseOrderId, dispatchId)
        : null,
    allowed: access.canDispatch && purchaseOrderId !== null && dispatchId !== null,
    permissions: DISPATCH_PERMISSIONS,
    load: async (cursor) => {
      const page = await fetchDispatchHistory(purchaseOrderId ?? '', dispatchId ?? '', cursor);
      return { ...page, data: page.events };
    },
  });
  const { pages, items: events, ...rest } = read;
  // The content is the same on every page; the first page's is the one shown.
  return { ...rest, events, content: pages[0]?.dispatch ?? null, pageSize: HISTORY_PAGE_SIZE };
}

export function useSupplierDispatchSettings(
  supplierId: string | null
): SingleRead<SupplierDispatchSettings> {
  const access = useDispatchAccess();
  return useSingleRead({
    key: access.owner && supplierId ? dispatchKeys.settings(access.owner, supplierId) : null,
    allowed: access.canReadSettings && supplierId !== null,
    permissions: SETTINGS_READ_PERMISSIONS,
    load: () => fetchSupplierDispatchSettings(supplierId ?? ''),
  });
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export type CommandResult<T> =
  | { status: 'success'; result: T }
  /** The server (or this client, before sending) decided: nothing happened because of this request. */
  | { status: 'refused'; error: unknown }
  /** The outcome is not known. The state was refreshed; the command is NOT repeated. */
  | { status: 'unknown'; error: unknown }
  /** An earlier outcome of the same command is still unacknowledged, or another request of it is on its way: nothing was sent. */
  | { status: 'blocked' }
  /** The session, tenant, user, generation or a needed permission changed: nothing may be reported to this screen. */
  | { status: 'canceled' };

function refreshOrder(client: QueryClient, owner: DispatchOwner, purchaseOrderId: string): void {
  void client.invalidateQueries({ queryKey: dispatchKeys.order(owner, purchaseOrderId) });
  void client.invalidateQueries({ queryKey: dispatchKeys.tenant(owner) });
}

interface GuardedConfig<TVars, TData> {
  kind: UnconfirmedKind;
  permissions: readonly string[];
  targetOf: (vars: TVars) => string;
  run: (vars: TVars) => Promise<TData>;
  /** Runs for the live owner after the answer, whatever it was: refresh what the server now says. */
  refresh: (client: QueryClient, owner: DispatchOwner, vars: TVars) => void;
  onSuccess?: (client: QueryClient, owner: DispatchOwner, data: TData, vars: TVars) => void;
}

export interface GuardedCommand<TVars, TData> {
  run: (vars: TVars) => Promise<CommandResult<TData>>;
  isPending: boolean;
}

/**
 * A write without a durable identity (cancel, resolve, settings save). One request at a time; after an UNKNOWN outcome the state is
 * refreshed, the outcome is marked unknown for its owner and target, and the same command is refused until the user acknowledges it
 * (`blocked`): nothing is repeated, and no new request is minted on its own.
 */
function useGuardedCommand<TVars, TData>(
  config: GuardedConfig<TVars, TData>
): GuardedCommand<TVars, TData> {
  const access = useDispatchAccess();
  const client = useQueryClient();
  const [pending, setPending] = useState(0);
  const busy = useRef(false);

  const run = async (vars: TVars): Promise<CommandResult<TData>> => {
    let fence: DispatchFence;
    try {
      fence = takeFence(access, config.permissions);
    } catch (error) {
      return error instanceof DispatchAccessError
        ? { status: 'refused', error }
        : { status: 'canceled' };
    }
    if (busy.current) return { status: 'blocked' };
    const target = config.targetOf(vars);
    if (hasUnconfirmed(fence.owner, config.kind, target)) return { status: 'blocked' };
    busy.current = true;
    setPending((count) => count + 1);
    const outcome: Outcome<TData> = await settle(() => config.run(vars));
    busy.current = false;
    setPending((count) => Math.max(count - 1, 0));

    const current = fenceHolds(fence);
    if (outcome.ok) {
      if (!current) return { status: 'canceled' };
      config.onSuccess?.(client, fence.owner, outcome.value, vars);
      config.refresh(client, fence.owner, vars);
      return { status: 'success', result: outcome.value };
    }
    if (isUnknownOutcome(outcome.error)) {
      // The owner's mark is kept even if the screen has moved on: it is inert for everyone else.
      addUnconfirmed(fence.owner, config.kind, target);
      if (!current) return { status: 'canceled' };
      config.refresh(client, fence.owner, vars);
      return { status: 'unknown', error: outcome.error };
    }
    if (!current) return { status: 'canceled' };
    config.refresh(client, fence.owner, vars);
    return { status: 'refused', error: outcome.error };
  };

  return { run, isPending: pending > 0 };
}

export interface Unconfirmed {
  mark: UnconfirmedCommand | null;
  acknowledge: () => void;
}

/** The unknown outcome of a cancel, resolve or settings save of `target`, until the user acknowledges it. */
export function useUnconfirmedCommand(kind: UnconfirmedKind, target: string | null): Unconfirmed {
  const access = useDispatchAccess();
  const mark = useUnconfirmed(access.owner, kind, target);
  return {
    mark,
    acknowledge: () => {
      if (access.owner !== null && target !== null)
        acknowledgeUnconfirmed(access.owner, kind, target);
    },
  };
}

interface CancelVars {
  purchaseOrderId: string;
  dispatchId: string;
  reason: string;
}

/** Cancel with a reason (`purchase_orders.dispatch`). A request that may have left stays as it is; the supplier is NOT told. */
export function useCancelDispatch(): GuardedCommand<CancelVars, DispatchCancelResult> {
  return useGuardedCommand<CancelVars, DispatchCancelResult>({
    kind: 'cancel',
    permissions: DISPATCH_PERMISSIONS,
    targetOf: (vars) => vars.dispatchId,
    run: (vars) => postDispatchCancel(vars.purchaseOrderId, vars.dispatchId, vars.reason),
    refresh: (client, owner, vars) => refreshOrder(client, owner, vars.purchaseOrderId),
  });
}

interface ResolveVars {
  purchaseOrderId: string;
  dispatchId: string;
  body: ResolveDispatchBody;
}

/** Explicit reconciliation of an uncertain dispatch (`purchase_orders.dispatch`). `not_sent` sends nothing. */
export function useResolveDispatch(): GuardedCommand<ResolveVars, DispatchResolveResult> {
  return useGuardedCommand<ResolveVars, DispatchResolveResult>({
    kind: 'resolve',
    permissions: DISPATCH_PERMISSIONS,
    targetOf: (vars) => vars.dispatchId,
    run: (vars) => postDispatchResolve(vars.purchaseOrderId, vars.dispatchId, vars.body),
    refresh: (client, owner, vars) => refreshOrder(client, owner, vars.purchaseOrderId),
  });
}

interface SettingsVars {
  supplierId: string;
  body: SupplierDispatchSettingsBody;
}

/** Saves the settings (`inventory.update` AND `purchase_orders.dispatch`). A save changes what a document would say: reviews go stale. */
export function useUpdateSupplierDispatchSettings(): GuardedCommand<
  SettingsVars,
  SupplierDispatchSettings
> {
  return useGuardedCommand<SettingsVars, SupplierDispatchSettings>({
    kind: 'settings',
    permissions: SETTINGS_WRITE_PERMISSIONS,
    targetOf: (vars) => vars.supplierId,
    run: (vars) => putSupplierDispatchSettings(vars.supplierId, vars.body),
    onSuccess: (client, owner, data, vars) => {
      client.setQueryData(dispatchKeys.settings(owner, vars.supplierId), data);
      bumpDocumentEpoch(owner);
    },
    refresh: (client, owner, vars) => {
      void client.invalidateQueries({ queryKey: dispatchKeys.settings(owner, vars.supplierId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/**
 * What the user is shown and may confirm: the server's answer plus what it was taken under. The document epoch fences a change of this
 * page's documents; `owner` and `generation` fence the session that took it. `submit` refuses a review whose owner or generation is not
 * the live one, so a review can never be confirmed by (or for) anybody else, whatever the caller does.
 */
export interface ReviewedPreview extends DispatchPreview {
  documentEpoch: number;
  readonly owner: DispatchOwner;
  readonly generation: number;
}

export class InvalidPreviewError extends Error {
  constructor() {
    super('The preview does not match the request');
    this.name = 'InvalidPreviewError';
  }
}

const SHA256 = /^[0-9a-f]{64}$/;

export interface PreviewCommand {
  run: (
    purchaseOrderId: string,
    channel: DispatchChannel
  ) => Promise<CommandResult<ReviewedPreview>>;
  isPending: boolean;
}

/**
 * Asks the server for the frozen document of a channel (`purchase_orders.dispatch`). Sends nothing to a supplier and changes nothing.
 * A preview is a read in effect: an unknown outcome is simply a failed read, and the user asks again.
 */
export function usePreviewDispatch(): PreviewCommand {
  const access = useDispatchAccess();
  const [pending, setPending] = useState(0);

  const run = async (
    purchaseOrderId: string,
    channel: DispatchChannel
  ): Promise<CommandResult<ReviewedPreview>> => {
    let fence: DispatchFence;
    try {
      fence = takeFence(access, DISPATCH_PERMISSIONS);
    } catch (error) {
      return error instanceof DispatchAccessError
        ? { status: 'refused', error }
        : { status: 'canceled' };
    }
    const documentEpoch = getDocumentEpoch(fence.owner);
    setPending((count) => count + 1);
    const outcome = await settle(() => postDispatchPreview(purchaseOrderId, channel));
    setPending((count) => Math.max(count - 1, 0));
    if (!fenceHolds(fence)) return { status: 'canceled' };
    if (!outcome.ok)
      return isDefiniteRefusal(outcome.error)
        ? { status: 'refused', error: outcome.error }
        : { status: 'unknown', error: outcome.error };
    const preview = outcome.value;
    if (
      preview.purchaseOrderId !== purchaseOrderId ||
      preview.channel !== channel ||
      preview.sendsNothing !== true ||
      !SHA256.test(preview.snapshotSha256)
    ) {
      return { status: 'refused', error: new InvalidPreviewError() };
    }
    return {
      status: 'success',
      result: Object.freeze({
        ...preview,
        documentEpoch,
        owner: fence.owner,
        generation: fence.epoch,
      }),
    };
  };

  return { run, isPending: pending > 0 };
}

// ---------------------------------------------------------------------------
// Enqueue: the keyed command
// ---------------------------------------------------------------------------

export type EnqueueResult =
  | { status: 'success'; result: DispatchEnqueueResult }
  /** The server refused; the key is released. The review must be done again (nothing is regenerated for the user). */
  | { status: 'refused'; error: unknown }
  /** The outcome is unknown. The exact command is kept; the user may send it again unchanged, or give it up knowingly. */
  | { status: 'uncertain'; error: unknown }
  | { status: 'busy' }
  | { status: 'blocked' }
  /**
   * The exact command could not be written down and read back, so NOTHING was sent (the request is only allowed after that). A new
   * command leaves no entry; a held one stays as it was, unchanged and not claimed durable. The user may try again explicitly.
   */
  | { status: 'not_durable' }
  /** The reviewed document is no longer current on this page: nothing was sent. */
  | { status: 'outdated' }
  | { status: 'canceled'; error?: unknown };

export class JournalFullError extends Error {
  constructor() {
    super('Too many unresolved dispatch commands');
    this.name = 'JournalFullError';
  }
}

export interface EnqueueCommand {
  /** Starts a new command with a new operation key from exactly what was reviewed. */
  submit: (review: ReviewedPreview) => Promise<EnqueueResult>;
  /** Sends the held command again: the same body and key, only for its own session. */
  retry: () => Promise<EnqueueResult>;
  /** Gives the held command up. Only an explicit, confirmed user action may call this. */
  discard: () => void;
  /** The command held after an unknown outcome (also one found again after a reload), if any. */
  held: JournalEntry | null;
  attempts: number;
  /** A command of this order is on its way, also one started by a screen that has since unmounted. */
  isPending: boolean;
  journalStatus: JournalStatus;
  discardDamaged: () => void;
}

export const newOperationKey = (): string => crypto.randomUUID();

export function useEnqueueDispatch(purchaseOrderId: string): EnqueueCommand {
  const access: DispatchAccess = useDispatchAccess();
  const client = useQueryClient();
  const view = useJournalView(access.owner);
  const slot = slotOf(purchaseOrderId);
  const entry = view.entries.find((candidate) => candidate.slot === slot) ?? null;
  const inFlight = view.inFlight.has(slot);
  // A first attempt still on its way is not yet "held": the screen shows progress, not a recovery.
  const held = entry && !(inFlight && entry.attempts <= 1) ? entry : null;

  const send = async (
    stored: JournalEntry,
    fence: DispatchFence,
    fresh: boolean
  ): Promise<EnqueueResult> => {
    const { owner } = stored;
    const key = stored.body.operationKey;
    if (!beginFlight(owner, slot, key)) {
      if (fresh) removeEntry(owner, slot, key);
      return { status: 'busy' };
    }
    // Durable before send: a fresh command was written (as its first attempt) and read back by `submit`; a repeat counts its attempt
    // here and may only leave when that count was kept as well. Otherwise nothing is sent, and the entry is left as it was.
    if (!fresh && !recordAttempt(owner, slot, key, 1)) {
      endFlight(owner, slot, key);
      return { status: 'not_durable' };
    }
    const raw = await settle(() =>
      runOwned(access, DISPATCH_PERMISSIONS, () =>
        postDispatchEnqueue(purchaseOrderId, stored.body)
      )
    );
    // A late answer carries its own outcome: that is what the server decided for THIS command.
    const late = !raw.ok && raw.error instanceof LateResultError;
    const outcome: Outcome<DispatchEnqueueResult> =
      !raw.ok && raw.error instanceof LateResultError
        ? (raw.error.outcome as Outcome<DispatchEnqueueResult>)
        : raw;
    const current = !late && fenceHolds(fence);

    let result: EnqueueResult;
    if (outcome.ok) {
      removeEntry(owner, slot, key);
      result = current ? { status: 'success', result: outcome.value } : { status: 'canceled' };
    } else if (isLocalRefusal(outcome.error)) {
      // Nothing was sent. A fresh command leaves nothing pending; a retry leaves the earlier unknown outcome exactly as it was.
      recordAttempt(owner, slot, key, -1);
      if (fresh) removeEntry(owner, slot, key);
      result = { status: 'canceled', error: outcome.error };
    } else if (isDefiniteRefusal(outcome.error)) {
      removeEntry(owner, slot, key);
      result = current ? { status: 'refused', error: outcome.error } : { status: 'canceled' };
    } else {
      // Unknown: the entry stays, for its owner.
      result = current ? { status: 'uncertain', error: outcome.error } : { status: 'canceled' };
    }
    endFlight(owner, slot, key);

    if (current) {
      if (result.status === 'success') bumpDocumentEpoch(owner);
      if (
        result.status === 'success' ||
        result.status === 'refused' ||
        result.status === 'uncertain'
      )
        refreshOrder(client, owner, purchaseOrderId);
    }
    return result;
  };

  const submit = async (review: ReviewedPreview): Promise<EnqueueResult> => {
    let fence: DispatchFence;
    try {
      fence = takeFence(access, DISPATCH_PERMISSIONS);
    } catch (error) {
      return { status: 'canceled', error };
    }
    if (review.purchaseOrderId !== purchaseOrderId) return { status: 'outdated' };
    // The review belongs to the session that took it: another owner, a new login or another generation cannot confirm it.
    if (!ownerEquals(review.owner, fence.owner) || review.generation !== fence.epoch)
      return { status: 'outdated' };
    // Fence before dispatch: a review taken before a change of this page's documents is not a review of what would be sent now.
    if (review.documentEpoch !== getDocumentEpoch(fence.owner)) return { status: 'outdated' };
    const current = getJournalView(fence.owner);
    if (current.status === 'corrupt') return { status: 'blocked' };
    if (current.inFlight.has(slot)) return { status: 'busy' };
    if (current.entries.some((candidate) => candidate.slot === slot)) return { status: 'blocked' };

    const body: DispatchEnqueueBody = {
      channel: review.channel,
      operationKey: newOperationKey(),
      reviewedSnapshotSha256: review.snapshotSha256,
    };
    const stored = putEntry(fence.owner, {
      purchaseOrderId,
      channel: review.channel,
      body,
      documentVersion: review.documentVersion,
      epoch: fence.epoch,
    });
    if (stored === 'conflict' || stored === 'corrupt') return { status: 'blocked' };
    if (stored === 'not_durable') return { status: 'not_durable' };
    if (stored === 'full') return { status: 'refused', error: new JournalFullError() };
    const written = getJournalView(fence.owner).entries.find(
      (candidate) => candidate.slot === slot
    );
    if (!written) return { status: 'blocked' };
    return send(written, fence, true);
  };

  const retry = async (): Promise<EnqueueResult> => {
    let fence: DispatchFence;
    try {
      fence = takeFence(access, DISPATCH_PERMISSIONS);
    } catch (error) {
      // The earlier outcome stays unknown and stays held.
      return { status: 'canceled', error };
    }
    const current = getJournalView(fence.owner);
    if (current.status === 'corrupt') return { status: 'blocked' };
    if (current.inFlight.has(slot)) return { status: 'busy' };
    const stored = current.entries.find((candidate) => candidate.slot === slot);
    if (!stored) return { status: 'blocked' };
    // Sent under another generation of this page: only that generation may resend it (a reload has no epoch and may).
    if (stored.epoch !== null && stored.epoch !== fence.epoch)
      return { status: 'canceled', error: new OwnerChangedError() };
    return send(stored, fence, false);
  };

  const discard = () => {
    let fence: DispatchFence;
    try {
      fence = takeFence(access, []);
    } catch {
      return;
    }
    const current = getJournalView(fence.owner);
    if (current.inFlight.has(slot)) return;
    const stored = current.entries.find((candidate) => candidate.slot === slot);
    if (stored) removeEntry(fence.owner, slot, stored.body.operationKey);
  };

  const discardDamaged = () => {
    try {
      dropCorruptJournal(takeFence(access, []).owner);
    } catch {
      // Not this owner's to discard.
    }
  };

  return {
    submit,
    retry,
    discard,
    held,
    attempts: entry?.attempts ?? 0,
    isPending: inFlight,
    journalStatus: view.status,
    discardDamaged,
  };
}

/** Every unresolved enqueue of the live owner (for a notice outside any one order's panel). */
export function useHeldEnqueues(): readonly JournalEntry[] {
  const access = useDispatchAccess();
  return useJournalView(access.owner).entries;
}

export { ownerId };
